import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, BadgeCheck, MapPin, Hash, UtensilsCrossed, Cpu, Sprout, HeartHandshake } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, EASE } from "@/components/Reveal";

const THALI_IMG = "https://images.unsplash.com/photo-1742281257687-092746ad6021?auto=format&fit=crop&w=1400&q=80";

function TiltHeroCard() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 90, damping: 16 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 90, damping: 16 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      data-testid="hero-tilt-card"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1000 }}
      className="relative mx-auto w-full max-w-md lg:max-w-none"
    >
      <div className="glass overflow-hidden rounded-3xl shadow-[0_40px_120px_rgba(0,0,0,0.55)]">
        <img
          src={THALI_IMG}
          alt="Thali served at Nok Chhonk"
          className="h-72 w-full object-cover md:h-80"
          loading="eager"
        />
        <div className="flex items-center justify-between px-6 py-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-400">Nok Chhonk · today&apos;s thali</p>
          <p className="font-mono text-xs text-amber-400">₹129 – ₹309</p>
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.7, ease: EASE }}
        className="glass-strong absolute -left-6 bottom-16 hidden items-center gap-3 rounded-2xl px-5 py-4 md:flex"
      >
        <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.9)]" />
        <div>
          <p className="text-xs font-semibold text-white">Nok Chhonk — live</p>
          <p className="text-[11px] text-slate-400">on Swiggy &amp; Zomato</p>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.7, ease: EASE }}
        className="glass-strong absolute -right-4 -top-5 hidden items-center gap-3 rounded-2xl px-5 py-4 md:flex"
      >
        <span className="h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_12px_rgba(6,182,212,0.9)]" />
        <div>
          <p className="text-xs font-semibold text-white">Mozekaql — building</p>
          <p className="text-[11px] text-slate-400">launching December 30, 2026</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

const BENTO = [
  {
    icon: UtensilsCrossed,
    tone: "amber",
    badge: "Active — live on Swiggy & Zomato",
    title: "Nok Chhonk",
    copy: "Our cloud kitchen serving authentic Odia & Bengali comfort food across Jaipur — the revenue engine of the ecosystem.",
    to: "/nokchhonk",
    span: "lg:col-span-7",
    img: THALI_IMG,
  },
  {
    icon: Cpu,
    tone: "cyan",
    badge: "Launching December 30, 2026",
    title: "Mozekaql",
    copy: "IT solution architecture & agro-research — food-tech software, enterprise cloud solutions and regional supply-chain analytics.",
    to: "/mozekaql",
    span: "lg:col-span-5",
  },
  {
    icon: Sprout,
    tone: "white",
    badge: "Research",
    title: "Agro-Research",
    copy: "Regional crop and supply studies that feed Mozekaql's analytics — and keep our menus seasonal.",
    span: "lg:col-span-4",
  },
  {
    icon: HeartHandshake,
    tone: "white",
    badge: "Community",
    title: "Foundation Initiatives",
    copy: "Community food and skill programmes funded by the profits the ecosystem earns.",
    span: "lg:col-span-4",
  },
];

const CREDITS = [
  { icon: ShieldCheck, label: "FSSAI Licensed" },
  { icon: BadgeCheck, label: "Startup India Recognized" },
  { icon: Hash, label: "CIN U55101RJ2025PTC109155" },
  { icon: MapPin, label: "Registered: Vaishali Nagar, Jaipur" },
];

export default function Home() {
  useEffect(() => {
    document.title = "TOSIASI CREATIVES — Food, Tech & Community Ventures";
  }, []);

  const { scrollY } = useScroll();
  const yGlow = useTransform(scrollY, [0, 700], [0, 140]);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative flex min-h-screen items-center pt-32 pb-20">
        <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="glow-amber absolute -left-40 top-10 h-[560px] w-[560px]" />
          <div className="glow-cyan absolute -right-40 top-40 h-[620px] w-[620px]" />
        </motion.div>
        <div className="bg-gridlines pointer-events-none absolute inset-0" aria-hidden="true" />

        <div className="relative mx-auto grid w-full max-w-7xl items-center gap-16 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="eyebrow"
            >
              TOSIASI Creatives Pvt. Ltd. — Est. Dec 2025 · Jaipur, Rajasthan
            </motion.p>

            <h1 className="mt-8 font-display text-[clamp(2.7rem,7.5vw,5.9rem)] font-semibold leading-[1.02] tracking-tight text-white">
              {[
                <span key="l1">Where a <span className="text-amber-400 text-glow-amber">kitchen</span></span>,
                <span key="l2">funds the <span className="text-cyan-300 text-glow-cyan">code</span></span>,
                <span key="l3" className="font-light text-slate-300">that feeds the future.</span>,
              ].map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "112%" }}
                    animate={{ y: "0%" }}
                    transition={{ duration: 1, delay: 0.25 + i * 0.14, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
            >
              A single venture ecosystem: our cloud kitchen <span className="text-amber-400">Nok Chhonk</span> funds
              <span className="text-cyan-300"> Mozekaql</span>&apos;s IT platforms — which in turn power agro-research
              and foundation initiatives.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/nokchhonk" data-testid="hero-cta-nokchhonk" className="btn btn-amber">
                Explore Nok Chhonk <ArrowRight size={17} />
              </Link>
              <Link to="/mozekaql" data-testid="hero-cta-mozekaql" className="btn btn-outline-cyan">
                Discover Mozekaql <ArrowUpRight size={17} />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: EASE }}
            className="lg:col-span-5"
          >
            <TiltHeroCard />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 md:block"
        >
          <div className="flex h-12 w-7 items-start justify-center rounded-full border border-white/15 p-1.5">
            <motion.span
              animate={{ y: [0, 14, 0], opacity: [1, 0.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
              className="h-2 w-2 rounded-full bg-amber-400"
            />
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* ECOSYSTEM BENTO */}
      <section className="relative py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">The venture ecosystem</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              One company. Four engines. <span className="font-light text-slate-400">A single flywheel.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-5 lg:grid-cols-12">
            {BENTO.map((b, i) => (
              <Reveal key={b.title} delay={0.08 * i} className={b.span}>
                <div
                  data-testid={`bento-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-hover glass group relative h-full overflow-hidden rounded-3xl ${
                    b.tone === "amber" ? "hover:border-amber-400/40" : b.tone === "cyan" ? "hover:border-cyan-400/40" : ""
                  }`}
                >
                  {b.tone === "amber" && (
                    <div className="glow-amber pointer-events-none absolute -right-24 -top-24 h-72 w-72 opacity-60" />
                  )}
                  {b.tone === "cyan" && (
                    <div className="glow-cyan pointer-events-none absolute -right-24 -top-24 h-72 w-72 opacity-60" />
                  )}
                  {b.img && (
                    <div className="relative h-52 overflow-hidden md:h-60">
                      <img
                        src={b.img}
                        alt={b.title}
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f18] via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="relative p-8">
                    <span
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                        b.tone === "amber"
                          ? "border-amber-400/30 bg-amber-400/10 text-amber-300"
                          : b.tone === "cyan"
                          ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-300"
                          : "border-white/15 bg-white/5 text-slate-300"
                      }`}
                    >
                      <b.icon size={12} /> {b.badge}
                    </span>
                    <h3 className="mt-5 font-display text-2xl font-semibold text-white">{b.title}</h3>
                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-400">{b.copy}</p>
                    {b.to && (
                      <Link
                        to={b.to}
                        data-testid={`bento-link-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`mt-6 inline-flex items-center gap-2 text-sm font-medium transition-transform duration-300 hover:translate-x-1 ${
                          b.tone === "amber" ? "text-amber-400" : b.tone === "cyan" ? "text-cyan-300" : "text-white"
                        }`}
                      >
                        {b.tone === "amber" ? "Visit the kitchen" : "Enter the lab"} <ArrowRight size={15} />
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Flow card */}
            <Reveal delay={0.24} className="lg:col-span-4">
              <div className="glass flex h-full flex-col justify-between rounded-3xl p-8">
                <p className="eyebrow">How the flywheel turns</p>
                <div className="mt-6 space-y-4">
                  {[
                    { n: "01", t: "Kitchen revenue", d: "Every thali & B2B contract earns" },
                    { n: "02", t: "Platform R&D", d: "That revenue funds Mozekaql's build" },
                    { n: "03", t: "Return to the source", d: "Tech supports agro-research & community" },
                  ].map((s, i) => (
                    <div key={s.n} className="relative">
                      {i < 2 && <span className="absolute left-[22px] top-11 h-8 w-px bg-gradient-to-b from-amber-400/60 to-cyan-400/60" />}
                      <div className="flex items-start gap-4">
                        <span className="glass flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-mono text-xs text-amber-400">
                          {s.n}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-white">{s.t}</p>
                          <p className="mt-0.5 text-xs text-slate-400">{s.d}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDENTIALS BAND */}
      <section className="border-y border-white/5 bg-white/[0.015] py-12">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-6 lg:grid-cols-4">
          {CREDITS.map((c, i) => (
            <Reveal key={c.label} delay={0.06 * i}>
              <div className="flex items-center gap-3" data-testid={`credential-${i}`}>
                <c.icon size={20} className="shrink-0 text-amber-400" />
                <p className="text-sm text-slate-300">{c.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32">
        <div className="glow-cyan pointer-events-none absolute left-1/2 top-1/2 h-[480px] w-[720px] -translate-x-1/2 -translate-y-1/2 opacity-70" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-white md:text-5xl">
              Taste the kitchen. <span className="font-light text-slate-400">Build the future.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-400">
              Order a thali tonight, or bring Mozekaql into your business early. Either way, you&apos;re in the
              flywheel.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/nokchhonk" data-testid="home-cta-order" className="btn btn-amber">
                Order from Nok Chhonk
              </Link>
              <Link to="/contact" data-testid="home-cta-partner" className="btn btn-outline-white">
                Partner with us <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
